import clsx from 'clsx';
import { useCanCreateWorkspace } from 'hooks/useUserInfo';
import React from 'react';
import { makeNewWorkspacePath, makePath } from 'utils/routes';
import { Link as WouterLink, useLocation } from 'wouter';
import { useWorkspace } from 'wrappers/WorkspaceProvider';
import { useAuth0 } from '@auth0/auth0-react';

import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';

import type { NavMode } from '../../Layout/PaymentsLayout';

const MENU_ITEM_CLASS = clsx(
  'flex items-center w-full text-xmd font-helvetica',
  'border-b-[0.5px] border-main-200 h-f4',
  'pl-[calc(0.5lh+3.5px)] pr-f1',
  'text-main-500 hover:text-main cursor-pointer'
);

type PaymentsNavMenuProps = {
  navMode: NavMode;
  onSwitchWorkspace: () => void;
  onClose: () => void;
};

export const PaymentsNavMenu = ({
  navMode,
  onSwitchWorkspace,
  onClose,
}: PaymentsNavMenuProps) => {
  const { logout } = useAuth0();
  const {
    workspaces,
    workspace: currentWorkspace,
    buildNewWorkspaceUrl,
  } = useWorkspace();
  const canCreateWorkspace = useCanCreateWorkspace(currentWorkspace?.id);

  const otherWorkspaces = workspaces.filter(
    (w) => w.id !== currentWorkspace?.id
  );

  const handleLogout = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const [, navigate] = useLocation();

  if (navMode === 'workspaces') {
    return (
      <div className="flex flex-col -mx-f1" data-testid="nav-workspace-list">
        {otherWorkspaces.map((ws) => (
          <WouterLink key={ws.id} href={`~${buildNewWorkspaceUrl(ws.id)}`}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <a title={ws.name} className={MENU_ITEM_CLASS} onClick={onClose}>
              <span className="truncate">{ws.name}</span>
            </a>
          </WouterLink>
        ))}
        {canCreateWorkspace && (
          <div className={clsx(MENU_ITEM_CLASS, '!border-b-0')}>
            <SecondaryButtonV3
              onClick={() => {
                onClose();
                navigate(makeNewWorkspacePath(currentWorkspace?.id));
              }}
              className="w-full text-xmd"
              data-testid="nav-new-workspace"
            >
              New Workspace +
            </SecondaryButtonV3>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col -mx-f1" data-testid="nav-menu">
      <button
        type="button"
        onClick={onSwitchWorkspace}
        className={MENU_ITEM_CLASS}
        data-testid="nav-switch-workspace"
      >
        Switch Workspace
      </button>
      <button
        type="button"
        onClick={() => {
          onClose();
          navigate(
            makePath(['w', currentWorkspace?.id ?? '', 'payments', 'settings'])
          );
        }}
        className={MENU_ITEM_CLASS}
        data-testid="nav-settings"
      >
        Settings
      </button>
      <button
        type="button"
        onClick={handleLogout}
        className={MENU_ITEM_CLASS}
        data-testid="nav-logout"
      >
        Logout
      </button>
    </div>
  );
};
